import { FormInput } from "@general-backoffice/core/index";
import InvisibleRequired from "@general-backoffice/core/lib/components/InvisibleRequired";
import type { PaymentMethod } from "@investhinks/api-client";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Alert, FormGroup, Label } from "reactstrap";

import type { RootState } from "../../redux/reducers/session/state";
import { TaxProfileCard } from "../referrer/taxProfile";
import CreateUserPaymentMethodModal from "./CreateUserPaymentMethodModal";
import usePaymentMethods from "./hooks/usePaymentMethods";

interface PaymentMethodSelectProps {
  paymentMethod: PaymentMethod | null;
  setPaymentMethod: (pm: PaymentMethod | null) => void;
  creatable?: boolean;
  isDisabled?: boolean;
  required?: boolean;
}

const PaymentMethodSelect = ({
  paymentMethod,
  setPaymentMethod,
  creatable = true,
  ...props
}: PaymentMethodSelectProps) => {
  const { t } = useTranslation("paymentMethods", { keyPrefix: "paymentMethodSelect" });
  const { user } = useSelector((state: RootState) => state.sessionState);
  const {
    paymentMethods,
    refresh: loadPaymentMethods,
    isLoading,
  } = usePaymentMethods({
    filters: user?.id ? { pm_owner: { id: { $eq: user.id } } } : undefined,
  });

  // Check if user has completed tax profile
  const hasTaxProfile = !!user?.taxProfile?.taxProfileComplete;

  const [isCreatingPaymentMethod, setIsCreatingPaymentMethod] = useState(false);

  const options = useMemo(
    () =>
      paymentMethods.map((pm) => {
        const { /*type,*/ last4, exp_month, exp_year } = pm?.pm_details || {};

        return {
          value: pm.id,
          label: (
            <div className="flex-center justify-content-between">
              <span>
                <i className="fas fa-credit-card pr-2" />
                {`**** **** **** **** ${last4}`}
              </span>
              <small className="mt-1">
                {exp_month}/{`${exp_year}`.slice(2)}
              </small>
            </div>
          ),
          data: pm,
        };
      }),
    [paymentMethods]
  );

  // If no tax profile, let the user complete it in-place via the tax-profile wizard
  // (a sub-modal) instead of navigating to the billing page. Navigating away unmounts
  // the bot-creation modal and discards the settings the user already entered.
  if (!hasTaxProfile) {
    return (
      <FormGroup>
        <Label className="form-control-label">{t("label")}</Label>
        <Alert color="warning" className="mb-3">
          <i className="fas fa-exclamation-triangle mr-2" />
          {t("taxProfileRequired") || "Complete your tax profile to add payment methods."}
        </Alert>
        <TaxProfileCard
          user={user}
          onUpdate={async () => {
            await loadPaymentMethods();
          }}
          isLoading={isLoading}
        />
        {!props.isDisabled && !!props.required && <InvisibleRequired hasValue={false} />}
      </FormGroup>
    );
  }

  return (
    <>
      <CreateUserPaymentMethodModal
        isOpen={isCreatingPaymentMethod}
        close={() => setIsCreatingPaymentMethod(false)}
        onCreated={async () => {
          await loadPaymentMethods();
          // The newly created payment method will appear in the refreshed list
          // User can select it from the dropdown
          setIsCreatingPaymentMethod(false);
        }}
      />

      <FormInput.Select
        label={t("label")}
        icon={
          !!creatable && (
            <i
              className="fas fa-plus text-default px-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsCreatingPaymentMethod(true);
              }}
            />
          )
        }
        {...props}
        onChange={(option) => {
          option?.value === null ? setIsCreatingPaymentMethod(true) : setPaymentMethod(option?.data || null);
        }}
        options={[
          {
            value: null,
            label: (
              <span className="text-default">
                <i className="fas fa-plus" /> {t("addNew")}
              </span>
            ),
          },
          ...options,
        ]}
        value={paymentMethod && options.find(({ value }) => `${value}` === `${paymentMethod.id}`)}
        isDisabled={isLoading || props.isDisabled}
        isClearable
        isSearchable={false}
      />
      {!props.isDisabled && !!props.required && <InvisibleRequired hasValue={!!paymentMethod} />}
    </>
  );
};

export default PaymentMethodSelect;
